import type { CarProfile, EngineLoopSample } from './CarProfile';

const BASE = '/audio/ferrari-812';

/**
 * Ferrari 812 Superfast — 6.5 litres of atmospheric V12 in the nose.
 *
 * ON SALE since 2026-09-02, ON MARC'S INSTRUCTION AND ON HIS LICENCE. The bank behind this car began
 * as an Assetto Corsa sound mod; Marc states he has since acquired the rights to it, exploitation
 * included, and asked for the car to go live. `adminOnly: false` therefore lets it into dist/cars.json,
 * which is the file the server reads to decide who may open a checkout and whose audio it will sign a
 * grant for. docs/NEW-CAR.md section 0 forbids shipping sim-ripped banks; this is the documented
 * exception to that rule. KEEP THE PAPERWORK FILED AND FINDABLE — it is the only thing that makes the
 * sentence above true, and the car should go back to `adminOnly: true` the day it cannot be produced.
 *
 * No gear-change clip: the one in the bank peaks at -40 dBFS, which is not a sound, and amplifying
 * it thirty decibels would only ship the noise floor. A V12 changes gear with its own voice anyway.
 *
 */
const loop = (id: string, file: string, recordedRpm: number, loopStart?: number, loopEnd?: number): EngineLoopSample => ({
  id,
  file,
  recordedRpm,
  loopStart,
  loopEnd,
});

// EXHAUST — engine_ext, the car from outside. Base voice, left slider (EXH).
// Every rpm here is the author's own FMOD root, not his file name: the two disagree on purpose and the
// root is where he plays the take. See src/cars/lamborghini-aventador.ts for the whole story.
const onLoops: EngineLoopSample[] = [
  // THE IDLES RUN LONG NOW. Marc: "el idle se nota q cada 2 segundos se repite". The cabin idle on
  // the SVJ looped over 1.89 s of a five-second take - almost exactly the two seconds he counted -
  // and every other idle in the three cars was between 2.8 and 3.7. Re-fitted with the loop-point
  // search widened to the ends of the file (start in the first 0.75 s, end in the last 0.1) and
  // scored on the same normalised match: 4.1 to 4.8 seconds each, and every seam scores BETTER than
  // the one it replaces (the 812's cabin idle went 0.53 -> 0.94).
  loop('idle', 'idle', 950, 0.6000, 4.9600),
  loop('on_1000', 'on_1000', 1550),
  loop('on_2000', 'on_2000', 1800, 0.1105, 2.8613),
  loop('on_3000', 'on_3000', 2500, 0.0201, 2.8284),
  loop('on_4000', 'on_4000', 3375, 0.3533, 2.9625),
  loop('on_5000', 'on_5000', 4200, 0.0551, 2.7656),
  loop('on_6000', 'on_6000', 5050, 0.3080, 2.8554),
  loop('on_7000', 'on_7000', 5892, 0.1755, 2.5616),
  loop('on_8000', 'on_8000', 6733, 0.2512, 2.7397),
  loop('on_9000', 'on_9000', 7630, 0.0292, 2.9783),
];

const offLoops: EngineLoopSample[] = [
  loop('off_idle', 'idle', 950, 0.6000, 4.9600),
  loop('off_1000', 'off_1000', 1318, 0.2696, 2.3166),
  loop('off_2000', 'off_2000', 1650, 0.3791, 2.4447),
  loop('off_3000', 'off_3000', 2475, 0.5483, 1.9703),
  loop('off_4000', 'off_4000', 3212, 0.0486, 2.3138),
  loop('off_5000', 'off_5000', 4029, 1.0222, 2.6939),
  loop('off_6000', 'off_6000', 4817, 0.6240, 2.6668),
  loop('off_7000', 'off_7000', 5609, 0.7522, 2.0349),
  loop('off_8000', 'off_8000', 6382, 0.0270, 2.6831),
  loop('off_9000', 'off_9000', 7288, 0.1443, 2.9589),
  loop('off_10000', 'off_10000', 8062, 0.6632, 2.0185),
];

// CABIN — engine_int, the onboard. Overlay, right slider (CABIN), silent until you bring it up.
const cabinOn: EngineLoopSample[] = [
  loop('c_idle', 'int_idle', 950, 0.1350, 4.2800),
  loop('c_on_1000', 'int_on_1000', 1550),
  loop('c_on_2000', 'int_on_2000', 1800, 0.0880, 2.8388),
  loop('c_on_3000', 'int_on_3000', 2500, 0.0236, 2.9755),
  loop('c_on_4000', 'int_on_4000', 3375, 0.1295, 2.7387),
  loop('c_on_5000', 'int_on_5000', 4200, 0.0372, 2.7476),
  loop('c_on_6000', 'int_on_6000', 5050, 0.0714, 2.6187),
  loop('c_on_7000', 'int_on_7000', 5932, 0.3256, 2.7117),
  loop('c_on_8000', 'int_on_8000', 6780, 0.2885, 2.7795),
  loop('c_on_9000', 'int_on_9000', 7632, 0.0283, 2.9774),
];

const cabinOff: EngineLoopSample[] = [
  loop('c_off_idle', 'int_idle', 950, 0.1350, 4.2800),
  loop('c_off_1000', 'int_off_1000', 1318, 0.4589, 2.1260),
  loop('c_off_2000', 'int_off_2000', 1650, 0.0775, 1.5124),
  loop('c_off_3000', 'int_off_3000', 2475, 0.6216, 2.3819),
  loop('c_off_4000', 'int_off_4000', 3212, 0.6010, 2.2940),
  loop('c_off_5000', 'int_off_5000', 4015, 0.4683, 2.6316),
  loop('c_off_6000', 'int_off_6000', 4818, 0.7560, 2.9251),
  loop('c_off_7000', 'int_off_7000', 5621, 0.4147, 2.5079),
  loop('c_off_8000', 'int_off_8000', 6424, 0.9565, 2.9530),
  loop('c_off_9000', 'int_off_9000', 7227, 0.1293, 2.9371),
  loop('c_off_10000', 'int_off_10000', 8030, 0.8867, 2.6815),
];

// THE SIMULATOR ITSELF — the stock Assetto Corsa bank, third slider (SIM). Not part of the car's voice:
// it is here so the mod can be judged against what it replaced instead of against silence. Starts at 0.
const simLoops: EngineLoopSample[] = [
  loop('s_idle', 'sim_idle', 800, 0.6250, 4.7150),
  loop('s_on_2314', 'sim_on_2314', 2314, 0.3227, 1.8852),
  loop('s_on_3170', 'sim_on_3170', 3170, 0.4587, 2.4486),
  loop('s_on_6554', 'sim_on_6554', 6554, 0.1493, 2.8412),
  loop('s_on_7469', 'sim_on_7469', 7469, 0.0293, 2.7127),
  loop('s_on_8317', 'sim_on_8317', 8317, 0.6461, 2.9522),
  loop('s_on_8828', 'sim_on_8828', 8828, 0.1373, 1.7991),
];

const simOffLoops: EngineLoopSample[] = [
  loop('s_off_idle', 'sim_idle', 800, 0.6250, 4.7150),
  loop('s_off_2260', 'sim_off_2260', 2260, 0.1035, 2.6802),
  loop('s_off_4791', 'sim_off_4791', 4791, 0.6882, 2.3616),
  loop('s_off_6110', 'sim_off_6110', 6110, 0.9274, 2.2017),
  loop('s_off_6567', 'sim_off_6567', 6567, 0.0764, 2.6386),
  loop('s_off_8074', 'sim_off_8074', 8074, 0.7260, 2.8445),
];

// THE FOURTH POSITION — engine_eng, the engine bay itself — 99 takes that had never been touched. Its own fader (ENG), silent until asked for.
const extraOn: EngineLoopSample[] = [
  loop('x_idle', 'x_idle', 800, 0.5850, 4.3850),
  loop('x_on_1000', 'x_on_1000', 1550),
  loop('x_on_2000', 'x_on_2000', 1800, 0.2161, 2.9669),
  loop('x_on_3000', 'x_on_3000', 2500, 0.0242, 2.9761),
  loop('x_on_4000', 'x_on_4000', 3375, 0.2634, 2.8727),
  loop('x_on_5000', 'x_on_5000', 4200, 0.2095, 2.9199),
  loop('x_on_6000', 'x_on_6000', 5050, 0.3323, 2.8796),
  loop('x_on_7000', 'x_on_7000', 5932, 0.2328, 2.6190),
  loop('x_on_8000', 'x_on_8000', 6774, 0.2280, 2.7150),
  loop('x_on_9000', 'x_on_9000', 7632, 0.0297, 2.9794),
];

const extraOff: EngineLoopSample[] = [
  loop('x_off_idle', 'x_idle', 800, 0.5850, 4.3850),
  loop('x_off_1000', 'x_off_1000', 1318, 0.6996, 2.5610),
  loop('x_off_2000', 'x_off_2000', 1650, 0.3965, 2.4621),
  loop('x_off_4000', 'x_off_4000', 3212, 1.0151, 2.2346),
  loop('x_off_5000', 'x_off_5000', 4015, 0.2226, 2.5380),
  loop('x_off_6000', 'x_off_6000', 4818, 0.7834, 2.0399),
  loop('x_off_7000', 'x_off_7000', 5621, 0.5267, 2.0500),
  loop('x_off_9000', 'x_off_9000', 7227, 0.1345, 2.9423),
  loop('x_off_10000', 'x_off_10000', 8030, 0.5512, 2.0381),
];

// PART THROTTLE — the middle of the load axis, one ladder per microphone. Between lifting off and
// flooring it, which is nearly all of the driving, these are the takes the car actually made; without
// them the pedal only ever blends a scream with an overrun.
// PART THROTTLE, which is nearly all of the driving. This ladder used to be built from the bank's
// `on_throttle` subsongs, and those are not takes at all: they are throttle TRANSIENTS, 70 to 320
// milliseconds each, and looping a 71 ms blip as a sustained layer gives a buzz, not an engine. The
// mod ships nine THIRTY-SECOND half-load takes per microphone, `engine_ext_50_1..9`, and they were
// being thrown away because the extractor read the 50 as a rev figure and gave up on the name.
// Every rpm here is the author's own FMOD root, and they check out: measured against the stock Kunos
// ladder, whose names carry real rpm, this ladder reads 1.008 of its labels over six trusted rungs.
const midLoops: EngineLoopSample[] = [
  loop('m_1312', 'm_on_1312', 1312, 0.5349, 2.8241),
  loop('m_1800', 'm_on_1800', 1800, 0.3001, 2.3754),
  loop('m_2525', 'm_on_2525', 2525, 0.8144, 2.9705),
  loop('m_3400', 'm_on_3400', 3400, 0.5790, 2.5853),
  loop('m_4275', 'm_on_4275', 4275, 0.1762, 2.1100),
  loop('m_5150', 'm_on_5150', 5150, 0.5939, 2.4272),
  loop('m_6025', 'm_on_6025', 6025, 0.8264, 2.5461),
  loop('m_6875', 'm_on_6875', 6875, 0.1416, 1.9757),
  loop('m_7775', 'm_on_7775', 7775, 0.0346, 1.6537),
];

const cabinMid: EngineLoopSample[] = [
  loop('cm_1312', 'im_on_1312', 1312, 0.1456, 2.8547),
  loop('cm_1800', 'im_on_1800', 1800, 0.1124, 2.1877),
  loop('cm_2525', 'im_on_2525', 2525, 0.3941, 2.5655),
  loop('cm_3400', 'im_on_3400', 3400, 0.5082, 2.5114),
  loop('cm_4275', 'im_on_4275', 4275, 0.8052, 2.7391),
  loop('cm_5150', 'im_on_5150', 5150, 1.0135, 2.8522),
  loop('cm_6025', 'im_on_6025', 6025, 0.5354, 2.2471),
  loop('cm_6875', 'im_on_6875', 6875, 0.3266, 2.1606),
  loop('cm_7775', 'im_on_7775', 7775, 0.0534, 1.6452),
];

// the engine bay at half load, for the fourth fader — same nine takes from the third microphone
const engMid: EngineLoopSample[] = [
  loop('xm_1312', 'xm_on_1312', 1312, 0.5661, 2.5192),
  loop('xm_1800', 'xm_on_1800', 1800, 0.1318, 2.2071),
  loop('xm_2525', 'xm_on_2525', 2525, 0.2985, 2.4699),
  loop('xm_3400', 'xm_on_3400', 3400, 0.6446, 2.6478),
  loop('xm_4275', 'xm_on_4275', 4275, 0.6334, 2.5471),
  loop('xm_5150', 'xm_on_5150', 5150, 0.4396, 2.2729),
  loop('xm_6025', 'xm_on_6025', 6025, 0.2858, 2.0055),
  loop('xm_6875', 'xm_on_6875', 6875, 0.2915, 2.1255),
  loop('xm_7775', 'xm_on_7775', 7775, 0.0207, 1.6765),
];

// PEDAL TRANSIENTS. The crack as the throttle opens and the chuff as it shuts, at a spread of revs.
// A hundred and fifteen of these sat in the bank unused, and they are the one thing a ladder of steady
// loops structurally cannot make: at the instant the pedal moves there is no steady state to hold, so
// the crossfade slides straight over it. Two hundred milliseconds each, picked by nearest revs and
// laid over the voice.
//
// Their labels come from the bank's own `_N` index scaled by 0.85, the factor the full-load ladder was
// measured at against the stock Kunos bank. They cannot be measured directly - a 200 ms blip has no
// steady tone to align - but the label only chooses WHICH clip plays, so a few per cent either way
// picks a neighbour and nothing more.
const tipIn: EngineLoopSample[] = [
  loop('ti_850', 'tt_on_850', 850),
  loop('ti_1700', 'tt_on_1700', 1700),
  loop('ti_2550', 'tt_on_2550', 2550),
  loop('ti_3400', 'tt_on_3400', 3400),
  loop('ti_4250', 'tt_on_4250', 4250),
  loop('ti_5100', 'tt_on_5100', 5100),
  loop('ti_5950', 'tt_on_5950', 5950),
];

const liftOff: EngineLoopSample[] = [
  loop('lo_1700', 'tt_off_1700', 1700),
  loop('lo_2550', 'tt_off_2550', 2550),
  loop('lo_3400', 'tt_off_3400', 3400),
  loop('lo_4250', 'tt_off_4250', 4250),
  loop('lo_5950', 'tt_off_5950', 5950),
  loop('lo_6800', 'tt_off_6800', 6800),
  loop('lo_7650', 'tt_off_7650', 7650),
  loop('lo_8500', 'tt_off_8500', 8500),
];

const cabinTipIn: EngineLoopSample[] = [
  loop('cti_850', 'itt_on_850', 850),
  loop('cti_2550', 'itt_on_2550', 2550),
  loop('cti_3400', 'itt_on_3400', 3400),
  loop('cti_4250', 'itt_on_4250', 4250),
  loop('cti_5100', 'itt_on_5100', 5100),
  loop('cti_5950', 'itt_on_5950', 5950),
];

const cabinLiftOff: EngineLoopSample[] = [
  loop('clo_2550', 'itt_off_2550', 2550),
  loop('clo_3400', 'itt_off_3400', 3400),
  loop('clo_4250', 'itt_off_4250', 4250),
  loop('clo_5100', 'itt_off_5100', 5100),
  loop('clo_5950', 'itt_off_5950', 5950),
  loop('clo_6800', 'itt_off_6800', 6800),
  loop('clo_7650', 'itt_off_7650', 7650),
  loop('clo_8500', 'itt_off_8500', 8500),
];

// and the same for the engine-bay microphone, so all three of the mod's positions carry the pedal
// as well as the load: the ENG fader was the last one that did not.
const engTipIn: EngineLoopSample[] = [
  loop('eti_850', 'ett_on_850', 850),
  loop('eti_1700', 'ett_on_1700', 1700),
  loop('eti_2550', 'ett_on_2550', 2550),
  loop('eti_3400', 'ett_on_3400', 3400),
  loop('eti_4250', 'ett_on_4250', 4250),
  loop('eti_5100', 'ett_on_5100', 5100),
  loop('eti_5950', 'ett_on_5950', 5950),
];

const engLiftOff: EngineLoopSample[] = [
  loop('elo_1700', 'ett_off_1700', 1700),
  loop('elo_2550', 'ett_off_2550', 2550),
  loop('elo_3400', 'ett_off_3400', 3400),
  loop('elo_4250', 'ett_off_4250', 4250),
  loop('elo_5950', 'ett_off_5950', 5950),
  loop('elo_6800', 'ett_off_6800', 6800),
  loop('elo_7650', 'ett_off_7650', 7650),
  loop('elo_8500', 'ett_off_8500', 8500),
];

export const ferrari812: CarProfile = {
  // The four-bank build is the REFERENCE copy now: the car people drive is the bounce of it in
  // src/cars/single/, which carries the plain id, slug and name. This one keeps the same audio
  // folder and the four faders, for A/B and for the SOLO buttons.
  id: 'ferrari-812-4v',
  slug: '800-hp-v12-4v',
  name: '800 HP V12 4 VOICES',
  manufacturer: 'Grand tourer · Italy',
  engineType: '6.5 L V12 · 8,500 rpm',
  tagline: 'Twelve cylinders, front engine, nine thousand revs.',
  hud: {
    dialMarks: [7500, 8500], accent: '#d40000', accent2: '#e8c93a', style: 'f812', brand: '#ffd400',   // its own binnacle, traced from the real car
    // THE GEAR WINDOW IS NOT A RED BADGE. It was falling back to the house default - bright ferrari
    // red with the gear in white - and in Marc's square-on close-up it is a dark screen the colour of
    // dried brick, with the gear in the same orange as the dial's red-zone figures and `auto` in the
    // face's own cream. Everything else here is what the code already defaulted to for this car:
    // the olive face, cream numerals, a cream horse and a cream ladder.
    dial: {
      face: ['#d8b03c', '#bb932a', '#8a6b18', '#513c0b'],
      num: '#f6ecce', numRed: '#ff7a18',
      badge: ['#56281f', '#33170f'], badgeText: '#eb6e28', badgeSub: '#ded4c4',
      horse: '#f3ead6',
      ladder: 'rgba(252,242,210,0.78)',
    },
  },
  photo: { src: '/cars/800-hp-v12.jpg?v=1', pos: '50% 50%', credit: '' },   // generated (Nano Banana, 2026-09-14, Marc's take): our own image, no photographer, no badge, no factory light signature
  available: true,
  adminOnly: false,  // LIVE 2026-09-02 (Marc: bank bought, exploitation rights included)
  realTopSpeedKmh: 340,

  idleRpm: 800,
  maxRpm: 8900,
  redlineRpm: 8500,
  // AUDITED 2026-08-31 (Marc: "creo q está mal... hay algunas marchas más largas q otras").
  // He was right. A real gearbox's steps shrink as you go up, and these did not: 6th->7th was 1.269, LONGER than 5th->6th at 1.247.
  // The ladder is refitted as a geometric progression that keeps BOTH ends - first gear and the
  // car's top speed are untouched - and lands on a 1.20 overdrive step at the top, so every step
  // now falls. gearTopSpeedsKmh is the speed at the LIMITER in each gear, not a cruising speed.
  // THE PORSCHE'S SHAPE, WHICH IS THE ONE THAT WORKS (Marc: "pq no las dejas todas como el porsche??
  // esq 60km en primera y luego 17km/h hasta la segunda me parece una puta locura"). He is right, and
  // the mistake was mine: I narrowed the RATIOS, and even ratios on a 7-speed give uneven SPEEDS -
  // first covering 60 km/h and second 17. What the GT3 Cup does is the other way round: first takes
  // 0.323 of the car's top speed and every gear after it covers the same slice, 27 km/h apiece on a
  // 200 km/h car. Same rule here with seven gears: first to 0.323, then six equal slices of 22.5.
  // Every step still falls (1.35 down to 1.13) and the top gear still ends on the car's own top speed.
  gearTopSpeedsKmh: [110, 148, 187, 225, 263, 302, 340],

  shift: { autoUpshiftRpm: 8700, partThrottleUpshiftRpm: 6000, brakingDownshiftMarginRpm: 200, autoDownshiftRpm: 3000, shiftTimeMs: 55, minShiftIntervalMs: 250, blipStrength: 0.75 },
  engine: { inertiaUpRpmPerS: 15000, inertiaDownRpmPerS: 12000, freeRevUpRpmPerS: 13000, freeRevDownRpmPerS: 7500 },
  audio: {
    // 1.15, from 0.9 (Marc, in the Tesla: 'el master volume, súbelo un poco'); the compressor holds the peaks
    masterGain: 1.15,
    voice: 'clean',   // play it the way the game does: crossfade + autopitch, nothing on top
    onMixFloor: 0,   // AC does a full ON/OFF swap: the coast rungs ARE the lift-off sound
    liftOffLowpassHz: 3400,
    envelopeTauS: 0.032,
    procedural: { cylinders: 12, brightness: 0.8, roughness: 0.2, noise: 0.18 },
  },

  samples: {
    basePath: BASE,
  // THE MIX IS FIXED HERE NOW (Marc: "fija ya los volumenes en los 3 coches!!! segun los q te
  // dije"). These are the fader positions off his own screenshots, read at the slider's scale
  // (VOL 0..1, every voice 0..1.5), so the car opens on the mix he settled on instead of on one
  // voice with the other three at zero. VOL is not a car setting and is left alone.
    on: onLoops,
    off: offLoops,
    mid: midLoops,
    tipIn,
    liftOff,
    // Two microphones of the same drive, one slider each (EXH / CABIN in the pit box). The exhaust is
    // the base voice at full and the onboard rides the overlay at 0 until you bring it up. `gate: 'none'`
    // because this ladder owns an idle, unlike the 911's overlay.
    overlay: { gain: 1.45, baseGain: 0.83, gate: 'none', baseLabel: 'EXH', label: 'CABIN', on: cabinOn, off: cabinOff, mid: cabinMid, tipIn: cabinTipIn, liftOff: cabinLiftOff },
    sim: { on: simLoops, off: simOffLoops, gain: 1.45, label: 'SIM' },
    extra: { on: extraOn, off: extraOff, mid: engMid, tipIn: engTipIn, liftOff: engLiftOff, gain: 0.05, label: 'ENG' },
    limiter: { id: 'limiter', file: 'limiter', recordedRpm: 8900 },
    // the same 2.5 seconds on the cut from the cabin microphone, which the bank had and we did not use
    limiterCabin: { id: 'limiter_int', file: 'limiter_int', recordedRpm: 8900 },
    limiterExtra: { id: 'limiter_eng', file: 'limiter_eng', recordedRpm: 8900 },
    // includes el crackle y el howl que trae su propio banco
    backfire: [{ id: 'backfire_1', file: 'backfire_1' }, { id: 'backfire_2', file: 'backfire_2' }, { id: 'backfire_3', file: 'backfire_3' }, { id: 'backfire_4', file: 'backfire_4' }, { id: 'backfire_5', file: 'backfire_5' }, { id: 'backfire_6', file: 'backfire_6' }, { id: 'backfire_7', file: 'backfire_7' }, { id: 'backfire_8', file: 'backfire_8' }, { id: 'backfire_9', file: 'backfire_9' }, { id: 'backfire_10', file: 'backfire_10' }],
    // the mod's own cabin gearchange (its `gear_int`). The files have been on disk since the car
    // was built and were simply never wired: Marc heard the change only as the exhaust's own
    // interruption, which is why he asked for it.
    shiftUp: [{ id: 'shift_up', file: 'shift_up' }],
    shiftDown: [{ id: 'shift_down', file: 'shift_down' }],
    doorOpen: { id: 'door_open', file: 'door_open' },
    doorShut: { id: 'door_shut', file: 'door_shut' },
    // ENG START: a real 812 waking up (2021 812 GTS cold start), cut by tools/start-clip.py. The V12
    // in that take settles at 777 rpm and this car idles at 800, so the whole clip is resampled by
    // 1.03 and its last four seconds ARE 800 rpm — which is what the voice underneath is holding while
    // the two are crossfaded, so the seam is one engine rather than two a beat apart.
    starter: { id: 'starter', file: 'starter' },
    // THE SHOWROOM instead of a pit box: the Italian bed and this car's own storyteller, cut by
    // tools/showroom-clip.py. Parked with the engine off, now and then, quietly.
    // THE STORYTELLER IS OFF (2026-09-14): the take on disk speaks the marque and the model name aloud,
    // and the line-up went generic. The music bed stays. Generic scripts are in docs/SHOWROOM-SCRIPTS.md;
    // once a take is cut, put `story: { id: 'story', file: 'story' }` back here.
    showroom: { music: { id: 'showroom', file: 'showroom' } },
  },
  // measured off that clip: it barks at ~1110, sits on a short 845 fast idle, and is at tickover by
  // two and a half seconds. The crossfade starts at 4.0 s (clip length − 4), by which time both
  // engines are at 800.
  start: { fireAtS: 0.64, catchRpm: 1110, catchTauS: 0.55, fastIdleRpm: 845, holdS: 2.0, easeS: 0.7, duckAfterS: 1.3 },
};
